import { ComponentProps, ReactNode } from 'react';
import ProductsAvailable from '@alltrails/amplitude/enums/ProductsAvailable';
import type PurchaseTier from '@alltrails/core/types/PurchaseTier';
import MultiStepModal from '@alltrails/modal/components/Modal/MultiStepModal';
import useMultiStepModal from '@alltrails/modal/hooks/useMultiStepModal';
import CheckoutModalStep from '../../CheckoutModalStep/CheckoutModalStep';
import CheckoutWelcome from '../../CheckoutWelcome/CheckoutWelcome';
import styles from './BaseUpsellModal.module.scss';
import BaseUpsellStep from './BaseUpsellStep';

type Props = {
  children?: ReactNode;
  headerImage?: ComponentProps<typeof BaseUpsellStep>['headerImage'];
  headerImageV3?: ComponentProps<typeof BaseUpsellStep>['headerImageV3'];
  heading: React.ReactNode;
  isOpen: boolean;
  isFocusPaused?: boolean;
  onCloseRequest: () => void;
  onSuccess?: () => void;
  primaryButtonText?: React.ReactNode;
  primaryButtonVariant?: ComponentProps<typeof BaseUpsellStep>['primaryButtonVariant'];
  promoEligibleHeading: React.ReactNode;
  tallerOnMobile?: boolean;
  upsellTrigger: ComponentProps<typeof BaseUpsellStep>['upsellTrigger'];
  trailId?: number;
  purchaseTier: PurchaseTier;
  productsAvailable: ProductsAvailable;
  skipCheckout?: boolean;
  isCrossPlatform?: boolean;
  onCrossPlatformRequest?: () => void;
};

export default function BaseUpsellModal({
  heading,
  promoEligibleHeading,
  headerImage,
  headerImageV3,
  isOpen,
  isFocusPaused = false,
  onCloseRequest,
  onSuccess,
  primaryButtonText,
  primaryButtonVariant,
  children,
  tallerOnMobile = false,
  upsellTrigger,
  trailId,
  purchaseTier,
  productsAvailable,
  skipCheckout = false,
  isCrossPlatform,
  onCrossPlatformRequest
}: Props) {
  const { activeModal, goBack, navigateTo } = useMultiStepModal<'upsell' | 'checkout' | 'welcome'>('upsell');

  return (
    <MultiStepModal
      isFullHeight={activeModal === 'checkout'}
      backgroundClassName={styles.containerOverOsano}
      initialFocus={false}
      isOpen={isOpen}
      isPaused={isFocusPaused || activeModal === 'checkout'} // Pause on checkout so Apple Pay desktop QR closes
      onCloseRequest={onCloseRequest}
      focusTrapOptions={{ clickOutsideDeactivates: false }}
      {...(activeModal !== 'welcome' ? { onBackRequest: goBack } : {})}
      {...(tallerOnMobile || activeModal === 'checkout' || activeModal === 'welcome' ? { modalClassName: styles.taller } : {})}
    >
      {activeModal === 'checkout' && (
        <CheckoutModalStep
          navigateToWelcome={() => navigateTo('welcome')}
          closeModal={onCloseRequest}
          onSuccess={onSuccess}
          purchaseTier={purchaseTier}
          trigger={upsellTrigger}
        />
      )}
      {activeModal === 'upsell' && (
        <BaseUpsellStep
          heading={heading}
          headerImage={headerImage}
          headerImageV3={headerImageV3}
          promoEligibleHeading={promoEligibleHeading}
          navigateTo={navigateTo}
          onCloseRequest={onCloseRequest}
          onSuccess={onSuccess}
          primaryButtonText={primaryButtonText}
          primaryButtonVariant={primaryButtonVariant}
          upsellTrigger={upsellTrigger}
          trailId={trailId}
          productType={purchaseTier === 'peak' ? 'peak' : 'plus'}
          productsAvailable={productsAvailable}
          skipCheckout={skipCheckout}
          isCrossPlatform={isCrossPlatform}
          onCrossPlatformRequest={onCrossPlatformRequest}
        >
          {children}
        </BaseUpsellStep>
      )}
      {activeModal === 'welcome' && <CheckoutWelcome closeModal={onCloseRequest} purchaseTier={purchaseTier} />}
    </MultiStepModal>
  );
}
